/**
 * Artist Detail Page
 *
 * Shows artist info, top songs, albums, singles.
 */

import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '@/api/client';
import { usePlayerStore } from '@/stores/player';
import { PlayIcon, ShuffleIcon, HeartOutlineIcon, ChevronRightIcon } from '@/components/icons';
import { MediaCard } from '@/components/common/MediaCard';
import { LoadingGrid } from '@/components/common/LoadingGrid';

export function ArtistPage() {
  const { id } = useParams<{ id: string }>();
  const [artist, setArtist] = useState<any>(null);
  const [songs, setSongs] = useState<any[]>([]);
  const [albums, setAlbums] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { play } = usePlayerStore();

  useEffect(() => {
    async function loadArtist() {
      if (!id) return;

      try {
        const response = await api.getArtist(id);
        if (response.data) {
          setArtist(response.data.artist);
          setSongs(response.data.songs as any[]);
          setAlbums(response.data.albums as any[]);
        }
      } catch (error) {
        console.error('Failed to load artist:', error);
      } finally {
        setIsLoading(false);
      }
    }

    loadArtist();
  }, [id]);

  const handlePlayAll = () => {
    if (songs.length > 0) {
      play(songs[0], songs);
    }
  };

  const handleShuffle = () => {
    if (songs.length > 0) {
      const shuffled = [...songs].sort(() => Math.random() - 0.5);
      play(shuffled[0], shuffled);
    }
  };

  if (isLoading) {
    return <LoadingGrid />;
  }

  if (!artist) {
    return (
      <div className="text-center py-12">
        <p className="text-on-surface-variant">Artist not found</p>
      </div>
    );
  }

  // Support both thumbnail array and direct string formats
  const thumbnail = artist.thumbnail || artist.thumbnails?.[artist.thumbnails.length - 1]?.url;

  return (
    <div className="min-h-full">
      {/* Hero header */}
      <header className="relative">
        {/* Background with blur */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${thumbnail})` }}
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-3xl" />
        </div>

        <div className="relative px-4 pt-8 pb-6 flex flex-col items-center text-center">
          {/* Artist image */}
          <img
            src={thumbnail || './pwa-192x192.webp'}
            alt={artist.name}
            className="w-32 h-32 sm:w-40 sm:h-40 rounded-full shadow-2xl object-cover mb-4"
            referrerPolicy="no-referrer"
            crossOrigin="anonymous"
            onError={(e) => {
              (e.target as HTMLImageElement).src = './pwa-192x192.webp';
            }}
          />

          {/* Artist info */}
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-1">
            {artist.name || artist.title}
          </h1>
          {artist.subscriberCount && (
            <p className="text-on-surface-variant mb-4">
              {artist.subscriberCount} subscribers
            </p>
          )}

          {/* Action buttons */}
          <div className="flex items-center gap-3">
            <button className="btn-primary flex items-center gap-2" onClick={handlePlayAll}>
              <PlayIcon className="w-5 h-5" />
              Play
            </button>
            <button className="btn-secondary flex items-center gap-2" onClick={handleShuffle}>
              <ShuffleIcon className="w-5 h-5" />
              Shuffle
            </button>
            <button className="btn-icon">
              <HeartOutlineIcon className="w-6 h-6" />
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="px-4 pb-8 space-y-8">
        {/* Top songs */}
        {songs.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">Top Songs</h2>
              {songs.length > 5 && (
                <button className="text-sm text-primary flex items-center gap-1">
                  See all
                  <ChevronRightIcon className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="divide-y divide-outline-variant/30">
              {songs.slice(0, 5).map((song, index) => (
                <button
                  key={song.id}
                  onClick={() => play(song, songs)}
                  className="w-full flex items-center gap-3 py-2 px-1 hover:bg-surface-container/50 transition-colors"
                >
                  <span className="w-6 text-center text-on-surface-variant text-sm shrink-0">
                    {index + 1}
                  </span>
                  <img
                    src={song.thumbnail || song.thumbnails?.[0]?.url || './pwa-192x192.webp'}
                    alt={song.title}
                    className="w-10 h-10 rounded object-cover shrink-0"
                    referrerPolicy="no-referrer"
                    crossOrigin="anonymous"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = './pwa-192x192.webp';
                    }}
                  />
                  <p className="flex-1 text-left font-medium line-clamp-1 text-sm">{song.title}</p>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Albums */}
        {albums.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">Albums</h2>
            </div>
            <div className="media-grid">
              {albums.map((album) => (
                <Link key={album.id} to={`/album/${album.id}`}>
                  <MediaCard item={album} variant="grid" />
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
